import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Thought } from '@/services/supabase/thoughts';
import React, { useEffect, useState } from 'react';

interface ThoughtEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (thought: Partial<Thought>) => void;
  thought?: Thought | null;
  isCreating?: boolean;
}

export const ThoughtEditorDialog: React.FC<ThoughtEditorDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  thought,
  isCreating = false
}) => {
  const { toast } = useToast();
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Initialize form with thought data when dialog opens
  useEffect(() => {
    if (thought) {
      setTitle(thought.title || '');
      setContent(thought.content || '');
    } else {
      // Default values for new thought
      setTitle('');
      setContent('');
    }
  }, [thought, isOpen]);

  // Handle save
  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Validate form
      if (!title.trim()) {
        toast({
          title: "Error",
          description: "Please provide a title",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      // Prepare thought data
      const thoughtData: Partial<Thought> = {
        title,
        content
      };

      // If editing, include the ID
      if (thought && !isCreating) {
        thoughtData.id = thought.id;
      }

      // Call the save handler
      await onSave(thoughtData);

      // Close the dialog
      onClose();

      // Show success toast
      toast({
        title: isCreating ? "Thought Created" : "Thought Updated",
        description: isCreating
          ? "Your thought has been created successfully"
          : "Your thought has been updated successfully",
      });
    } catch (error) {
      console.error('Error saving thought:', error);
      toast({
        title: "Error",
        description: "Failed to save thought. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 py-5 border-b space-y-0">
          <div className="flex items-center justify-between mr-6">
            <DialogTitle asChild>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Thought Title"
                className="text-xl font-semibold border-0 px-0 h-auto focus-visible:ring-0 placeholder:text-muted-foreground/50 bg-transparent"
                autoFocus
              />
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-6 py-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your thought here..."
                className="min-h-[250px] resize-none border-0 focus-visible:ring-0 p-0 text-base leading-relaxed placeholder:text-muted-foreground/50 bg-transparent"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-muted/50 flex justify-end space-x-2">
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : (isCreating ? 'Create Thought' : 'Save Changes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
